import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageSquare, Smartphone, QrCode, CheckCircle2, XCircle, Loader2,
  RefreshCw, LogOut, Send, Phone, WifiOff, ShieldCheck, Activity,
  ChevronDown, ChevronUp, AlertTriangle, Users, Settings as SettingsIcon,
  Mail, Trash2, Pencil, Lock,
} from 'lucide-react';
import AdminLayout from '../../../components/Admin/Layout/AdminLayout';
import FooterBar from '../../../components/shared/FooterBar';
import { useAuth } from '../../../context/AuthContext';
import whatsappService from '../../../services/whatsappService';
import userService from '../../../services/userService';
import amEmailSenderService from '../../../services/amEmailSenderService';

const STATUS_CONFIG = {
  connected:    { icon: CheckCircle2, label: 'Connected',     bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', border: 'border-emerald-200' },
  qr_ready:     { icon: QrCode,       label: 'Scan QR Code',  bg: 'bg-[#FFF4E6]',  text: 'text-[#C45500]',   dot: 'bg-[#f08804]',  border: 'border-[#FFD89E]' },
  connecting:   { icon: Loader2,      label: 'Connecting...', bg: 'bg-blue-50',    text: 'text-blue-700',    dot: 'bg-blue-500',   border: 'border-blue-200' },
  disconnected: { icon: WifiOff,      label: 'Not Linked',    bg: 'bg-[#F7F7F7]',  text: 'text-[#565959]',   dot: 'bg-[#999]',     border: 'border-[#D5D9D9]' },
  error:        { icon: XCircle,      label: 'Error',         bg: 'bg-red-50',     text: 'text-red-700',     dot: 'bg-red-500',    border: 'border-red-200' },
};

function formatPhone(phone) {
  if (!phone) return '—';
  const p = String(phone).replace(/\D/g, '');
  if (p.length >= 12) return `+${p.slice(0, 2)} ${p.slice(2, 7)} ${p.slice(7)}`;
  return `+${p}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ── Link Panel ────────────────────────────────────────────────────────────
// QR is NEVER auto-generated. Nothing here calls /qr until the user clicks
// "Generate QR Code" (onStart). That keeps the gateway from booting a Baileys
// socket on every page visit.
function LinkPanel({ linking, qrData, qrMessage, linkNote, onStart, onRefresh, onCancel }) {
  if (!linking) {
    return (
      <div className="flex flex-col items-center py-8">
        <div className="w-16 h-16 rounded-lg bg-[#F7F7F7] flex items-center justify-center border border-[#D5D9D9]">
          <QrCode size={32} className="text-[#999]" />
        </div>
        <p className="mt-3 text-[12px] text-[#565959] max-w-xs text-center">
          Generate a QR code, then scan it from WhatsApp on your phone to link your number.
        </p>
        <button onClick={onStart}
          className="mt-4 flex items-center gap-2 px-4 py-[9px] bg-[#f08804] text-white text-[13px] font-semibold rounded-lg hover:bg-[#d97706] transition-colors">
          <QrCode size={15} /> Generate QR Code
        </button>
        {linkNote && <p className="mt-3 text-[11px] text-[#C45500]">{linkNote}</p>}
      </div>
    );
  }
  if (qrData) {
    return (
      <div className="flex flex-col items-center py-6">
        <div className="p-4 bg-white rounded-lg shadow-sm border border-[#D5D9D9]">
          <img src={qrData} alt="WhatsApp QR Code" className="w-64 h-64 rounded" />
        </div>
        <div className="mt-4 text-center max-w-xs">
          <p className="text-[13px] font-semibold text-[#0F1111]">Open WhatsApp on your phone</p>
          <p className="text-[11px] text-[#565959] mt-1">
            Go to <span className="font-medium text-[#0F1111]">Settings → Linked Devices → Link a Device</span> and scan this code
          </p>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={onRefresh} className="flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] hover:text-[#0F1111] transition-colors">
            <RefreshCw size={12} /> Refresh QR
          </button>
          <button onClick={onCancel} className="px-2.5 py-[7px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] hover:text-[#0F1111] transition-colors">
            Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center py-8">
      <Loader2 size={36} className="text-[#f08804] animate-spin" />
      <p className="mt-3 text-[12px] text-[#565959] max-w-xs text-center">{qrMessage || 'Preparing QR code…'}</p>
      <button onClick={onCancel} className="mt-3 px-2.5 py-[7px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] hover:text-[#0F1111] transition-colors">
        Cancel
      </button>
    </div>
  );
}

function ConnectedDevice({ info, onLogout, isLoggingOut }) {
  return (
    <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-emerald-100 flex items-center justify-center flex-shrink-0">
          <Smartphone size={20} className="text-emerald-700" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-semibold text-[#0F1111]">Your linked number</h3>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Active
            </span>
          </div>
          <p className="text-[12px] text-[#565959] mt-0.5">{formatPhone(info.phone)}</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="bg-white rounded-md px-3 py-2 border border-[#E7E7E7]">
          <p className="text-[10px] text-[#565959] uppercase tracking-wide font-semibold">Connected Since</p>
          <p className="text-[12px] font-medium text-[#0F1111] mt-0.5">
            {info.connectedAt ? new Date(info.connectedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
          </p>
        </div>
        <div className="bg-white rounded-md px-3 py-2 border border-[#E7E7E7]">
          <p className="text-[10px] text-[#565959] uppercase tracking-wide font-semibold">Reconnects</p>
          <p className="text-[12px] font-medium text-[#0F1111] mt-0.5">{info.reconnectAttempts ?? 0}</p>
        </div>
      </div>
      <button onClick={onLogout} disabled={isLoggingOut}
        className="mt-3 flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50">
        {isLoggingOut ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />}
        {isLoggingOut ? 'Disconnecting...' : 'Disconnect'}
      </button>
    </div>
  );
}

function TestMessage({ sessionId, isConnected }) {
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('Hello! This is a test notification from eCommNotify. ✅');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const handleSend = async () => {
    if (!phone.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const fullPhone = phone.trim().startsWith('91') ? phone.trim() : '91' + phone.trim();
      const res = await whatsappService.send(sessionId, fullPhone, message);
      setResult({ type: 'success', text: `Message sent to ${formatPhone(res.phone || fullPhone)}` });
      setPhone('');
    } catch (err) {
      setResult({ type: 'error', text: err.response?.data?.error || err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#E7E7E7] flex items-center gap-2 bg-[#F7F7F7]">
        <Send size={14} className="text-[#f08804]" />
        <h3 className="text-[13px] font-semibold text-[#0F1111]">Test Message</h3>
      </div>
      <div className="p-4 space-y-3">
        <div className="flex">
          <div className="flex items-center gap-1.5 px-2.5 py-[7px] rounded-l-lg border border-r-0 border-[#D5D9D9] bg-[#F7F7F7] text-[13px] font-semibold text-[#0F1111]">
            <Phone size={12} /><span>+91</span>
          </div>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
            placeholder="10-digit mobile number" maxLength={10} disabled={!isConnected}
            className="flex-1 px-2.5 py-[7px] border rounded-r-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] disabled:bg-[#F7F7F7] disabled:text-[#999]" />
        </div>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} disabled={!isConnected}
          className="w-full px-2.5 py-2 border border-[#D5D9D9] rounded-lg text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] resize-none disabled:bg-[#F7F7F7] disabled:text-[#999]" />
        <button onClick={handleSend} disabled={!isConnected || sending || phone.trim().length < 10}
          className="w-full flex items-center justify-center gap-2 px-3 py-[9px] bg-[#f08804] text-white text-[13px] font-semibold rounded-lg hover:bg-[#d97706] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          {sending ? 'Sending...' : 'Send Test Message'}
        </button>
        {result && (
          <div className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] ${result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
            {result.type === 'success' ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {result.text}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageLogs({ sessionId, isConnected }) {
  const [logs, setLogs] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const loadLogs = useCallback(async () => {
    try { setLogs(await whatsappService.getLogs(sessionId, 20)); } catch { setLogs(null); }
  }, [sessionId]);

  useEffect(() => { if (expanded && isConnected) loadLogs(); }, [expanded, isConnected, loadLogs]);

  return (
    <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[#F7FAFA] transition-colors">
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[#f08804]" />
          <h3 className="text-[13px] font-semibold text-[#0F1111]">Message Log</h3>
          {logs?.stats && <span className="text-[11px] text-[#565959]">{logs.stats.totalSent} sent · {logs.stats.totalFailed} failed</span>}
        </div>
        {expanded ? <ChevronUp size={14} className="text-[#999]" /> : <ChevronDown size={14} className="text-[#999]" />}
      </button>
      {expanded && (
        <div className="border-t border-[#E7E7E7] max-h-64 overflow-y-auto divide-y divide-[#F1F1F1]">
          {logs?.messages?.length > 0 ? logs.messages.map((msg) => (
            <div key={msg.id} className="px-4 py-2 flex items-center gap-3 hover:bg-[#F7FAFA]">
              {msg.status === 'sent' ? <CheckCircle2 size={13} className="text-emerald-500" /> : <XCircle size={13} className="text-red-500" />}
              <span className="text-[12px] text-[#0F1111] font-mono">{formatPhone(msg.phone)}</span>
              <span className="text-[11px] text-[#999] ml-auto">{timeAgo(msg.timestamp)}</span>
            </div>
          )) : <div className="px-4 py-6 text-center text-[12px] text-[#999]">No messages yet</div>}
        </div>
      )}
    </div>
  );
}

// ── WhatsApp Link view (per logged-in user) ───────────────────────────────
// /qr BOOTS a Baileys socket on the gateway, so it is called ONLY on an
// explicit button press (Generate / Refresh QR) — never on a timer. After a
// QR is shown, only /status (cheap, in-memory) is polled to detect the scan.
const MAX_LINK_ATTEMPTS = 40; // scan-watch cap: ~2.5 min of /status at 4s

function WhatsAppLinkView({ sessionId }) {
  const [status, setStatus] = useState('disconnected');
  const [info, setInfo] = useState({});
  const [qrData, setQrData] = useState(null);
  const [qrMessage, setQrMessage] = useState(null);
  const [linkNote, setLinkNote] = useState(null);
  const [linking, setLinking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const slowPollRef = useRef(null);
  const linkPollRef = useRef(null);
  const linkAttemptsRef = useRef(0);
  const statusRef = useRef(status);
  const linkingRef = useRef(linking);

  useEffect(() => { statusRef.current = status; }, [status]);
  useEffect(() => { linkingRef.current = linking; }, [linking]);

  const applyStatus = (data) => {
    setServiceError(null);
    setStatus(data.linked ? 'connected' : data.status);
    setInfo(data);
  };

  // Status-only check — does NOT call /qr, so it never boots a socket.
  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setServiceError('Your account id is unavailable. Please log out and log in again.');
      setLoading(false);
      return null;
    }
    try {
      const data = await whatsappService.getStatus(sessionId);
      applyStatus(data);
      if (data.linked) setQrData(null);
      return data;
    } catch (err) {
      setServiceError(
        err.message?.includes('Network Error')
          ? 'WhatsApp service is not reachable. Please try again shortly.'
          : `Service error: ${err.message}`);
      setStatus('error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const stopLinkPoll = () => {
    if (linkPollRef.current) { clearTimeout(linkPollRef.current); linkPollRef.current = null; }
  };

  // After a QR is shown, watch ONLY /status (cheap, in-memory — never boots a
  // socket) to detect when the user has scanned it. This NEVER calls /qr.
  const runScanWatch = () => {
    stopLinkPoll();
    linkPollRef.current = setTimeout(async () => {
      if (!sessionId) return;
      linkAttemptsRef.current += 1;
      try {
        const data = await whatsappService.getStatus(sessionId);
        applyStatus(data);
        if (data.linked) {
          setQrData(null); setQrMessage(null); setLinking(false); stopLinkPoll();
          return;
        }
      } catch { /* keep watching */ }
      if (linkAttemptsRef.current >= MAX_LINK_ATTEMPTS) {
        stopLinkPoll();
        setLinkNote('QR not scanned in time — tap "Refresh QR" for a new code.');
        return;
      }
      runScanWatch();
    }, 4000);
  };

  // Calls /qr EXACTLY ONCE. Used only by the Generate and Refresh QR buttons.
  // No timer ever calls /qr — if it fails or the QR isn't ready, we stop and
  // wait for the user to press the button again.
  const requestQr = async () => {
    setLinkNote(null);
    setServiceError(null);
    setQrData(null);
    setQrMessage('Starting WhatsApp session…');
    setLinking(true);
    linkAttemptsRef.current = 0;
    stopLinkPoll();
    try {
      const qr = await whatsappService.getQR(sessionId); // boots the gateway session
      if (qr.status === 'already_connected') {
        await checkStatus(); setLinking(false); return;
      }
      if (qr.qr) {
        setQrData(qr.qr); setQrMessage(null);
        runScanWatch(); // status-only watch for the scan — does NOT call /qr
        return;
      }
      // QR not ready yet — do NOT auto-retry. User taps the button again.
      setLinking(false);
      setLinkNote(qr.message
        ? `${qr.message} Tap "Generate QR Code" again in a few seconds.`
        : 'QR not ready yet. Tap "Generate QR Code" again in a few seconds.');
    } catch {
      setLinking(false);
      setLinkNote('Could not reach WhatsApp service. Tap "Generate QR Code" to try again.');
    }
  };

  const startLinking = requestQr;

  const cancelLinking = async () => {
    stopLinkPoll();
    setLinking(false);
    setQrData(null);
    setQrMessage(null);
    // Tear down the half-started session so it doesn't linger on the gateway.
    try { await whatsappService.logout(sessionId); } catch { /* ignore */ }
    checkStatus();
  };

  // Mount: one status check. Then a heartbeat that calls /status whenever we're
  // NOT in the middle of a QR-linking flow (which runs its own scan watch).
  // /status is cheap and in-memory — it never boots a socket — so polling it
  // while disconnected/reconnecting is safe and lets the page auto-reflect a
  // background self-heal (the gateway now reconnects dropped sessions on its
  // own) without the user having to refresh.
  useEffect(() => {
    checkStatus();
    const tick = () => {
      slowPollRef.current = setTimeout(async () => {
        if (sessionId && !linkingRef.current) {
          try {
            const data = await whatsappService.getStatus(sessionId);
            applyStatus(data);
            if (data.linked) setQrData(null);
          } catch { /* ignore */ }
        }
        tick();
      }, 25000);
    };
    tick();
    return () => {
      if (slowPollRef.current) clearTimeout(slowPollRef.current);
      stopLinkPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const handleLogout = async () => {
    if (!window.confirm('Disconnect your WhatsApp number? You will need to scan the QR again to reconnect.')) return;
    setIsLoggingOut(true);
    try {
      await whatsappService.logout(sessionId);
      setStatus('disconnected'); setInfo({}); setQrData(null);
      setTimeout(checkStatus, 2000);
    } catch (err) {
      alert('Failed to disconnect: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Manual refresh = one more explicit /qr request (same single-call path).
  const handleRefreshQR = () => { requestQr(); };

  // Force an immediate reconnect of an already-linked session that has dropped.
  // Uses /reconnect (resets backoff, reuses saved creds — no QR scan needed),
  // so it only makes sense when a session already exists and is recovering.
  const handleReconnect = async () => {
    if (!sessionId) return;
    setReconnecting(true);
    try {
      await whatsappService.reconnect(sessionId);
      setTimeout(checkStatus, 2000);
    } catch { /* status poll will reflect the outcome */ }
    finally { setReconnecting(false); }
  };

  const isConnected = status === 'connected';
  // A session that is 'connecting' while we're NOT driving a QR-link flow is a
  // previously-linked session the gateway is auto-recovering. Show a dedicated
  // "Reconnecting…" panel instead of the "Link your WhatsApp" prompt, which
  // would wrongly imply the number was never linked.
  const bgReconnecting = status === 'connecting' && !linking;
  const sc = STATUS_CONFIG[status] || STATUS_CONFIG.disconnected;
  const StatusIcon = sc.icon;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-[#0F1111]">Your WhatsApp</h2>
          <p className="text-[12px] text-[#565959]">Link your number — sellers you manage receive their alerts from it.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 px-2 py-[3px] rounded text-[11px] font-semibold border ${sc.bg} ${sc.text} ${sc.border}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot} ${isConnected ? 'animate-pulse' : ''}`} />
            {sc.label}
          </span>
          <button onClick={checkStatus} className="p-1.5 rounded text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111] transition-colors" title="Refresh">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {serviceError && (
        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-[12px] font-semibold text-red-800">Service Unavailable</p>
            <p className="text-[11px] text-red-600 mt-0.5 font-mono">{serviceError}</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center py-16">
          <Loader2 size={32} className="text-[#f08804] animate-spin" />
          <p className="mt-3 text-[12px] text-[#565959]">Checking WhatsApp connection...</p>
        </div>
      ) : (
        <>
          <div className={`rounded-lg border overflow-hidden ${isConnected ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#D5D9D9]'}`}>
            <div className="p-4">
              {isConnected ? (
                <ConnectedDevice info={info} onLogout={handleLogout} isLoggingOut={isLoggingOut} />
              ) : bgReconnecting ? (
                <div className="flex flex-col items-center py-8">
                  <Loader2 size={32} className="text-[#f08804] animate-spin" />
                  <h3 className="mt-3 text-[15px] font-semibold text-[#0F1111]">Reconnecting…</h3>
                  <p className="mt-1 text-[12px] text-[#565959] max-w-xs text-center">
                    Your WhatsApp link dropped and is reconnecting automatically — no need to scan again. This usually clears on its own.
                  </p>
                  <button onClick={handleReconnect} disabled={reconnecting}
                    className="mt-4 flex items-center gap-2 px-4 py-[9px] bg-[#f08804] text-white text-[13px] font-semibold rounded-lg hover:bg-[#d97706] transition-colors disabled:opacity-50">
                    {reconnecting ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
                    {reconnecting ? 'Reconnecting…' : 'Reconnect now'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-center mb-2">
                    <div className="w-12 h-12 rounded-lg bg-[#F7F7F7] shadow-sm flex items-center justify-center mx-auto mb-3 border border-[#D5D9D9]">
                      <StatusIcon size={24} className={qrData ? 'text-[#f08804]' : 'text-[#999]'} />
                    </div>
                    <h3 className="text-[15px] font-semibold text-[#0F1111]">
                      {qrData ? 'Scan QR Code to Link' : linking ? 'Generating QR Code' : 'Link Your WhatsApp'}
                    </h3>
                    <p className="text-[12px] text-[#565959] mt-1 max-w-sm mx-auto">
                      Link your personal WhatsApp — no business account needed.
                    </p>
                  </div>
                  <LinkPanel
                    linking={linking}
                    qrData={qrData}
                    qrMessage={qrMessage}
                    linkNote={linkNote}
                    onStart={startLinking}
                    onRefresh={handleRefreshQR}
                    onCancel={cancelLinking}
                  />
                </>
              )}
            </div>
          </div>

          {isConnected && <TestMessage sessionId={sessionId} isConnected={isConnected} />}
          {isConnected && <MessageLogs sessionId={sessionId} isConnected={isConnected} />}

          <div className="flex items-start gap-2.5 p-3 bg-[#FFF4E6] border border-[#FFD89E] rounded-lg">
            <ShieldCheck size={16} className="text-[#C45500] mt-0.5 flex-shrink-0" />
            <ul className="text-[11px] text-[#8B5A00] space-y-0.5 list-disc list-inside">
              <li>Messages are sent from your linked number — recipients see your number</li>
              <li>Keep daily volume modest to avoid WhatsApp restrictions</li>
              <li>Session auto-expires after ~14 days of inactivity — just re-scan</li>
              <li>Sellers you are the Account Manager for use this number for their WhatsApp alerts</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

// ── Team linked numbers (inline, admin / management only) ─────────────────
function TeamLinkedNumbers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [usersRes, sessionsRes] = await Promise.all([
          userService.getAllUsers(),
          whatsappService.getSessions().catch(() => ({ sessions: [] })),
        ]);
        const users = Array.isArray(usersRes) ? usersRes : (usersRes?.data || usersRes?.users || []);
        const sessions = sessionsRes?.sessions || [];
        const byId = {};
        sessions.forEach((s) => { if (s.branchId) byId[s.branchId] = s; });
        const team = users
          .filter((u) => (u.userType || u.raw?.userType) !== 'SELLER_USER')
          .map((u) => {
            const id = u.id || u.raw?.id;
            const s = byId[id];
            return {
              id,
              name: (u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()) || u.email || '—',
              userType: u.userType || u.raw?.userType || '—',
              linked: !!s?.linked,
              phone: s?.phone || null,
              connectedAt: s?.connectedAt || null,
            };
          })
          .sort((a, b) => a.name.localeCompare(b.name));
        if (!cancelled) setRows(team);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    const matchesName = !q || r.name.toLowerCase().includes(q);
    const matchesStatus =
      statusFilter === 'all' || (statusFilter === 'linked' ? r.linked : !r.linked);
    return matchesName && matchesStatus;
  });
  const linkedCount = rows.filter((r) => r.linked).length;

  return (
    <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#E7E7E7] flex items-center gap-2 bg-[#F7F7F7]">
        <Users size={14} className="text-[#f08804]" />
        <h3 className="text-[13px] font-semibold text-[#0F1111]">Team WhatsApp numbers</h3>
        {!loading && (
          <span className="text-[11px] text-[#565959] ml-auto">
            {linkedCount} linked · {rows.length} members
          </span>
        )}
      </div>

      {!loading && rows.length > 0 && (
        <div className="px-4 py-2 border-b border-[#F1F1F1] flex flex-col sm:flex-row gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name…"
            className="flex-1 px-2.5 py-[6px] border rounded-lg border-[#D5D9D9] text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2.5 py-[6px] border rounded-lg border-[#D5D9D9] text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
            <option value="all">All statuses</option>
            <option value="linked">Linked</option>
            <option value="not_linked">Not linked</option>
          </select>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px] text-[#999]">No team members found.</div>
      ) : filtered.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px] text-[#999]">No members match your filters.</div>
      ) : (
        <div className="max-h-72 overflow-y-auto divide-y divide-[#F1F1F1]">
          {filtered.map((r) => (
            <div key={r.id} className="px-4 py-2.5 flex items-center gap-3 hover:bg-[#F7FAFA]">
              {r.linked
                ? <CheckCircle2 size={14} className="text-emerald-500 flex-shrink-0" />
                : <WifiOff size={14} className="text-[#999] flex-shrink-0" />}
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-[#0F1111] truncate">{r.name}</p>
                <p className="text-[11px] text-[#565959]">{r.userType}</p>
              </div>
              {r.linked ? (
                <>
                  <span className="ml-auto text-[12px] text-slate-600 font-mono">{formatPhone(r.phone)}</span>
                  <span className="text-[11px] text-[#999] w-16 text-right">{r.connectedAt ? timeAgo(r.connectedAt) : ''}</span>
                </>
              ) : (
                <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F7F7F7] text-[#565959] border border-[#D5D9D9]">Not linked</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Email Sender view (per logged-in account manager) ─────────────────────
// Lets an AM set the mailbox their seller notifications are sent FROM. SMTP
// host/port come from server config; only email + password are stored (the
// password is validated by an SMTP login and saved AES-encrypted server-side).
function EmailSenderView() {
  const [status, setStatus] = useState(null); // CredentialStatus | null
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [result, setResult] = useState(null); // { type, text }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await amEmailSenderService.getStatus();
      setStatus(s);
      if (s?.email) { setEmail(s.email); setTestTo(s.email); }
      setEditing(!s); // no credential yet → show the form
    } catch {
      setStatus(null);
      setEditing(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!email.trim() || !password) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await amEmailSenderService.save(email.trim(), password);
      setResult({ type: res.valid ? 'success' : 'error', text: res.message });
      setPassword('');
      if (res.valid) setEditing(false);
      await load();
    } catch (err) {
      setResult({ type: 'error', text: err.response?.data?.message || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = await amEmailSenderService.sendTest(testTo.trim() || undefined);
      setResult({ type: res.valid ? 'success' : 'error', text: res.message });
    } catch (err) {
      setResult({ type: 'error', text: err.response?.data?.message || err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your sending email? Your sellers will then receive notifications from the default address.')) return;
    try {
      await amEmailSenderService.remove();
      setStatus(null); setEmail(''); setPassword(''); setResult(null); setEditing(true);
    } catch (err) {
      alert('Failed to remove: ' + (err.response?.data?.message || err.message));
    }
  };

  const validated = status?.validated;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-[#0F1111]">Your sending email</h2>
          <p className="text-[12px] text-[#565959]">Sellers you manage receive their email alerts from this address.</p>
        </div>
        {status && (
          <span className={`inline-flex items-center gap-1.5 px-2 py-[3px] rounded text-[11px] font-semibold border ${
            validated ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${validated ? 'bg-emerald-500' : 'bg-red-500'}`} />
            {validated ? 'Validated' : 'Not validated'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col items-center py-16">
          <Loader2 size={32} className="text-[#f08804] animate-spin" />
          <p className="mt-3 text-[12px] text-[#565959]">Loading email settings...</p>
        </div>
      ) : (!editing && status) ? (
        // ── Configured view ──
        <div className={`rounded-lg border p-4 ${validated ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-[#D5D9D9]'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center flex-shrink-0 border border-[#E7E7E7]">
              <Mail size={20} className={validated ? 'text-emerald-700' : 'text-[#999]'} />
            </div>
            <div className="min-w-0">
              <h3 className="text-[13px] font-semibold text-[#0F1111] truncate">{status.email}</h3>
              <p className="text-[11px] text-[#565959] mt-0.5">
                {validated
                  ? `Validated${status.validatedAt ? ' · ' + timeAgo(status.validatedAt) : ''}`
                  : 'Saved, but the SMTP login could not be verified — re-enter the password.'}
              </p>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            <div>
              <label className="block text-[10px] font-semibold text-[#565959] uppercase tracking-wide mb-1">
                Send a test email to
              </label>
              <div className="flex items-center gap-2">
                <input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                  placeholder="recipient@example.com"
                  className="flex-1 px-2.5 py-[7px] border rounded-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
                <button onClick={handleTest} disabled={testing || !testTo.trim()}
                  className="flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-white bg-[#f08804] rounded-lg hover:bg-[#d97706] transition-colors disabled:opacity-50 whitespace-nowrap">
                  {testing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                  {testing ? 'Sending...' : 'Send test'}
                </button>
              </div>
              <p className="text-[10px] text-[#999] mt-1">Sent from {status.email} — use your own or a colleague's address to confirm it arrives.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setEditing(true); setPassword(''); setResult(null); }}
                className="flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] hover:text-[#0F1111] transition-colors">
                <Pencil size={12} /> Change
              </button>
              <button onClick={handleRemove}
                className="flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors">
                <Trash2 size={12} /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        // ── Form view ──
        <div className="bg-white rounded-lg border border-[#D5D9D9] p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold text-[#565959] uppercase tracking-wide mb-1">Email address</label>
            <div className="flex">
              <div className="flex items-center px-2.5 py-[7px] rounded-l-lg border border-r-0 border-[#D5D9D9] bg-[#F7F7F7]">
                <Mail size={13} className="text-[#999]" />
              </div>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@loombiz.com"
                className="flex-1 px-2.5 py-[7px] border rounded-r-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-[#565959] uppercase tracking-wide mb-1">Password</label>
            <div className="flex">
              <div className="flex items-center px-2.5 py-[7px] rounded-l-lg border border-r-0 border-[#D5D9D9] bg-[#F7F7F7]">
                <Lock size={13} className="text-[#999]" />
              </div>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Mailbox password (or app password)" autoComplete="new-password"
                className="flex-1 px-2.5 py-[7px] border rounded-r-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving || !email.trim() || !password}
              className="flex items-center justify-center gap-2 px-3 py-[9px] bg-[#f08804] text-white text-[13px] font-semibold rounded-lg hover:bg-[#d97706] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {saving ? 'Validating...' : 'Validate & Save'}
            </button>
            {status && (
              <button onClick={() => { setEditing(false); setPassword(''); setResult(null); }}
                className="px-2.5 py-[9px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] hover:text-[#0F1111] transition-colors">
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className={`flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] ${
          result.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
          {result.type === 'success' ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {result.text}
        </div>
      )}

      <div className="flex items-start gap-2.5 p-3 bg-[#FFF4E6] border border-[#FFD89E] rounded-lg">
        <ShieldCheck size={16} className="text-[#C45500] mt-0.5 flex-shrink-0" />
        <ul className="text-[11px] text-[#8B5A00] space-y-0.5 list-disc list-inside">
          <li>Emails to your sellers are sent from this address — they see it as the sender</li>
          <li>Your password is stored encrypted and is never shown again</li>
          <li>If it stops working, notifications automatically fall back to the default address</li>
        </ul>
      </div>
    </div>
  );
}

// ── Team email senders (admin / management) ──────────────────────────────
// Lists team members with their sending-email status and lets an admin
// configure / test / remove any account manager's mailbox.
function TeamEmailSenders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // userId being edited
  const [form, setForm] = useState({ email: '', password: '' });
  const [busyId, setBusyId] = useState(null);
  const [msg, setMsg] = useState(null); // { type, text }
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, credsRes] = await Promise.all([
        userService.getAllUsers(),
        amEmailSenderService.getAll().catch(() => []),
      ]);
      const users = Array.isArray(usersRes) ? usersRes : (usersRes?.data || usersRes?.users || []);
      const creds = Array.isArray(credsRes) ? credsRes : (credsRes?.data || []);
      const byUser = {};
      creds.forEach((c) => { if (c.userId) byUser[c.userId] = c; });
      const team = users
        .filter((u) => (u.userType || u.raw?.userType) !== 'SELLER_USER')
        .map((u) => {
          const id = u.id || u.raw?.id;
          const c = byUser[id];
          return {
            id,
            name: (u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim()) || u.email || '—',
            userType: u.userType || u.raw?.userType || '—',
            email: c?.email || null,
            validated: !!c?.validated,
            enabled: c?.enabled ?? false,
            configured: !!c,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
      setRows(team);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = (row) => { setEditing(row.id); setForm({ email: row.email || '', password: '' }); setMsg(null); };
  const cancelEdit = () => { setEditing(null); setForm({ email: '', password: '' }); };

  const save = async (userId) => {
    if (!form.email.trim() || !form.password) return;
    setBusyId(userId); setMsg(null);
    try {
      const res = await amEmailSenderService.saveFor(userId, form.email.trim(), form.password);
      setMsg({ type: res.valid ? 'success' : 'error', text: res.message });
      if (res.valid) cancelEdit();
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message });
    } finally { setBusyId(null); }
  };

  const test = async (userId) => {
    setBusyId(userId); setMsg(null);
    try {
      const res = await amEmailSenderService.sendTestFor(userId);
      setMsg({ type: res.valid ? 'success' : 'error', text: res.message });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.message || err.message });
    } finally { setBusyId(null); }
  };

  const remove = async (userId, name) => {
    if (!window.confirm(`Remove ${name}'s sending email? Their sellers will fall back to the default address.`)) return;
    setBusyId(userId); setMsg(null);
    try { await amEmailSenderService.removeFor(userId); await load(); }
    catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || err.message }); }
    finally { setBusyId(null); }
  };

  const statusBadge = (r) => {
    if (!r.configured) return <span className="text-[11px] text-[#999]">Not configured</span>;
    if (!r.enabled) return <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F7F7F7] text-[#565959] border border-[#D5D9D9]">Disabled</span>;
    return r.validated
      ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle2 size={10} /> Validated</span>
      : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200"><XCircle size={10} /> Not validated</span>;
  };

  const statusKeyOf = (r) =>
    !r.configured ? 'not_configured' : !r.enabled ? 'disabled' : r.validated ? 'validated' : 'not_validated';
  const q = query.trim().toLowerCase();
  const filtered = rows.filter((r) => {
    const matchesName = !q || r.name.toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || statusKeyOf(r) === statusFilter;
    return matchesName && matchesStatus;
  });

  return (
    <div className="bg-white rounded-lg border border-[#D5D9D9] overflow-hidden">
      <div className="px-4 py-2.5 border-b border-[#E7E7E7] flex items-center gap-2 bg-[#F7F7F7]">
        <Users size={14} className="text-[#f08804]" />
        <h3 className="text-[13px] font-semibold text-[#0F1111]">Team sending emails</h3>
        {!loading && (
          <span className="text-[11px] text-[#565959] ml-auto">
            {rows.filter((r) => r.configured && r.validated).length} active · {rows.length} members
          </span>
        )}
      </div>

      {!loading && rows.length > 0 && (
        <div className="px-4 py-2 border-b border-[#F1F1F1] flex flex-col sm:flex-row gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name or email…"
            className="flex-1 px-2.5 py-[6px] border rounded-lg border-[#D5D9D9] text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2.5 py-[6px] border rounded-lg border-[#D5D9D9] text-[12px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]">
            <option value="all">All statuses</option>
            <option value="validated">Validated</option>
            <option value="not_validated">Not validated</option>
            <option value="disabled">Disabled</option>
            <option value="not_configured">Not configured</option>
          </select>
        </div>
      )}

      {msg && (
        <div className={`mx-4 mt-3 flex items-center gap-2 px-2.5 py-2 rounded-lg text-[12px] ${
          msg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
          {msg.type === 'success' ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
          {msg.text}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 size={22} className="animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px] text-[#999]">No team members found.</div>
      ) : filtered.length === 0 ? (
        <div className="px-4 py-8 text-center text-[12px] text-[#999]">No members match your filters.</div>
      ) : (
        <div className="max-h-96 overflow-y-auto divide-y divide-[#F1F1F1]">
          {filtered.map((r) => (
            <div key={r.id} className="px-4 py-2.5">
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium text-[#0F1111] truncate">{r.name}</p>
                  <p className="text-[11px] text-[#565959]">
                    {r.userType}{r.email ? ` · ${r.email}` : ''}
                  </p>
                </div>
                {statusBadge(r)}
                <div className="flex items-center gap-1">
                  {r.configured && (
                    <button onClick={() => test(r.id)} disabled={busyId === r.id} title="Send test email"
                      className="p-1.5 rounded text-[#565959] hover:bg-[#F7F7F7] hover:text-[#f08804] transition-colors disabled:opacity-50">
                      {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    </button>
                  )}
                  <button onClick={() => (editing === r.id ? cancelEdit() : startEdit(r))} title={r.configured ? 'Change' : 'Configure'}
                    className="p-1.5 rounded text-[#565959] hover:bg-[#F7F7F7] hover:text-[#0F1111] transition-colors">
                    <Pencil size={13} />
                  </button>
                  {r.configured && (
                    <button onClick={() => remove(r.id, r.name)} disabled={busyId === r.id} title="Remove"
                      className="p-1.5 rounded text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              {editing === r.id && (
                <div className="mt-2 flex flex-col sm:flex-row gap-2">
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="mailbox@loombiz.com"
                    className="flex-1 px-2.5 py-[7px] border rounded-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
                  <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="Password" autoComplete="new-password"
                    className="flex-1 px-2.5 py-[7px] border rounded-lg border-[#D5D9D9] text-[13px] bg-white focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]" />
                  <div className="flex items-center gap-2">
                    <button onClick={() => save(r.id)} disabled={busyId === r.id || !form.email.trim() || !form.password}
                      className="flex items-center gap-1.5 px-2.5 py-[7px] text-[12px] text-white bg-[#f08804] rounded-lg hover:bg-[#d97706] transition-colors disabled:opacity-50 whitespace-nowrap">
                      {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Validate & Save
                    </button>
                    <button onClick={cancelEdit}
                      className="px-2.5 py-[7px] text-[12px] text-[#565959] bg-white border border-[#D5D9D9] rounded-lg hover:border-[#888] transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page (Settings shell + left rail) ─────────────────────────────────────
const SETTINGS_RAIL = [
  { key: 'whatsapp', label: 'WhatsApp Link', icon: MessageSquare },
  { key: 'email', label: 'Email Sender', icon: Mail },
];

export default function SettingsWhatsAppPage() {
  const { user, isAdminOrManagement } = useAuth();
  const sessionId = user?.id;
  const canSeeTeam = typeof isAdminOrManagement === 'function' && isAdminOrManagement();
  const [active, setActive] = useState('whatsapp');

  return (
    <AdminLayout>
      <div className="min-h-full flex flex-col">
        <div className="bg-white border-b border-[#D5D9D9] px-4 sm:px-6 py-2 flex items-center gap-2 sticky top-0 z-10">
          <SettingsIcon size={15} className="text-[#565959]" />
          <h1 className="text-[14px] font-bold text-[#0F1111]">Settings</h1>
        </div>

        <div className="flex-1 flex flex-col sm:flex-row">
          {/* Left rail */}
          <div className="sm:w-56 flex-shrink-0 border-b sm:border-b-0 sm:border-r border-[#D5D9D9] bg-white">
            <nav className="p-2 sm:p-3 flex sm:flex-col gap-1">
              {SETTINGS_RAIL.map((it) => {
                const Icon = it.icon;
                const isActive = active === it.key;
                return (
                  <button key={it.key} onClick={() => setActive(it.key)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-medium transition-colors text-left ${
                      isActive ? 'bg-[#FFF4E6] text-[#C45500] border border-[#FFD89E]' : 'text-[#565959] hover:bg-[#F7F7F7] border border-transparent'
                    }`}>
                    <Icon size={15} /> {it.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-3 sm:p-5">
            <div className="max-w-2xl space-y-3">
              {active === 'whatsapp' && (
                <>
                  <WhatsAppLinkView sessionId={sessionId} />
                  {canSeeTeam && <TeamLinkedNumbers />}
                </>
              )}
              {active === 'email' && (
                <>
                  <EmailSenderView />
                  {canSeeTeam && <TeamEmailSenders />}
                </>
              )}
            </div>
          </div>
        </div>

        <FooterBar totalPages={1} currentPage={1} onPageChange={() => {}} totalItems={0} pageSize={20} />
      </div>
    </AdminLayout>
  );
}
